import React from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardCheck, 
  Brain, 
  BookOpen, 
  Briefcase,
  ChevronRight
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import AuthModal from './auth/AuthModal';
import { useNavigate } from 'react-router-dom';
import SuccessStories from './SuccessStories';

const steps = [
  {
    icon: ClipboardCheck,
    title: 'Quiz Predyspozycji',
    description: 'Wypełnij krótki quiz, który pomoże nam poznać Twoje zainteresowania i umiejętności'
  },
  {
    icon: Brain,
    title: 'Analiza AI',
    description: 'Nasz system AI analizuje Twój profil i dopasowuje najlepsze ścieżki kariery'
  },
  {
    icon: BookOpen,
    title: 'Spersonalizowany Plan',
    description: 'Otrzymujesz dokładny plan rozwoju z dopasowanymi kursami i materiałami'
  },
  {
    icon: Briefcase,
    title: 'Wsparcie w Karierze',
    description: 'Pomagamy w znalezieniu pierwszej pracy i dalszym rozwoju zawodowym'
  }
];

export default function HowWeWork() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const handleStartJourney = () => {
    if (user) {
      // Jeśli użytkownik jest zalogowany, przekieruj do quizu
      navigate('/dashboard');
    } else {
      // Jeśli nie jest zalogowany, pokaż modal rejestracji
      setShowAuthModal(true);
    }
  };

  return (
    <section className="py-6 bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Jak Działamy?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Prosta droga do Twojej wymarzonej kariery w 4 krokach
          </p>
        </motion.div>

        <div className="relative">
          {/* Linia łącząca */}
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-purple-100 -translate-y-1/2 hidden md:block" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative group"
              >
                <div className="flex flex-col items-center">
                  {/* Numer kroku */}
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mb-6 relative z-10 shadow-lg group-hover:shadow-xl transition-shadow"
                  >
                    {index + 1}
                  </motion.div>

                  {/* Ikona i treść */}
                  <div className="text-center">
                    <div className="mb-4">
                      <step.icon className="w-8 h-8 text-purple-600 mx-auto" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>

                  {/* Strzałka do następnego kroku */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
                      <motion.div
                        animate={{ x: [0, 10, 0] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                      >
                        <ChevronRight className="w-6 h-6 text-purple-400" />
                      </motion.div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <button 
            onClick={handleStartJourney}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-medium text-lg hover:from-purple-700 hover:to-indigo-700 transition-all transform hover:scale-105"
          >
            {user ? 'Rozpocznij Quiz' : 'Rozpocznij Swoją Podróż'}
          </button>
        </motion.div>
      </div>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />

      <div className="mt-16">
        <SuccessStories />
      </div>
    </section>
  );
}
